import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiX } from 'react-icons/fi'
import { formatDateToDDMMYYYY } from '@/utils/dateFormat'

interface PeanutLotDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  data: {
    product: string
    productDescription: string
    lot: string
    bags: string
    bagsCapacity: string
    bagsUnit: string
    bagsProgress: number
    bagsProgressColor: 'red' | 'green'
    location: string
    label: string | null
    coa: string | null
    buyContractNumber?: string
    saleContractNumber?: string
    lastUpdated: string
    status: string
  } | null
}

const PeanutLotDetailsModal = ({ isOpen, onClose, data }: PeanutLotDetailsModalProps) => {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen])

  if (!isOpen || !data) return null

  const statusStyles = (() => {
    if (data.status === 'Dispatched') {
      return { bg: '#E8F7EF', text: '#13AB5B' }
    }
    return { bg: '#EAF1FF', text: '#2C71F6' }
  })()

  const progressColor = data.bagsProgressColor === 'green' ? '#22C55E' : '#EF4444'

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      style={{ overflow: 'hidden' }}
    >
      <div 
        className="bg-white modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '400px',
          height: '100vh',
          maxHeight: '100vh',
          position: 'fixed',
          right: '0',
          top: '0',
          transform: 'translateX(0) rotate(0deg)',
          transition: 'transform 0.3s ease-out',
          opacity: 1,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <style>{`
          .scrollable-content {
            overflow-y: auto;
          }
        `}</style>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0" style={{ backgroundColor: '#F2F4F2' }}>
          <h2
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              color: '#212126'
            }}
          >
            Peanut Lot Details
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid #D1D5DB',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiX className="w-4 h-4" style={{ color: '#333333' }} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div 
          className="scrollable-content" 
          style={{ 
            flex: 1, 
            overflowY: 'auto',
            overflowX: 'hidden',
            height: 'auto'
          }}
        >
          {/* Details Content */}
          <div className="p-6 space-y-0" style={{ backgroundColor: '#FFFFFF' }}>
          {/* Product */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'start',
              paddingBottom: '16px',
              paddingTop: '0px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Product
            </label>
            <div className="flex flex-col items-start" style={{ textAlign: 'left' }}>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '13px',
                  lineHeight: '22px',
                  color: '#212126',
                  display: 'block',
                  marginBottom: '4px'
                }}
              >
                {data.product}
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#69697A',
                  textAlign: 'left'
                }}
              >
                {data.productDescription}
              </span>
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Lot */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'center',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Lot
            </label>
            <div
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '20px',
                color: '#212126',
                textAlign: 'left'
              }}
            >
              {data.lot}
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Bags */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'center',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Bags
            </label>
            <div className="flex flex-col gap-1 items-start">
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: '13px',
                  lineHeight: '20px',
                  color: '#212126',
                  textAlign: 'left'
                }}
              >
                <span style={{ fontWeight: 700 }}>{data.bags}</span>{' '}
                <span style={{ fontWeight: 400 }}>{data.bagsUnit}</span>
              </span>
              <div className="w-24 bg-gray-200 rounded-full h-1.5" style={{ backgroundColor: '#E5E7EB', width: '96px' }}>
                <div
                  className="h-1.5 rounded-full"
                  style={{
                    width: `${data.bagsProgress}%`,
                    backgroundColor: progressColor,
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Location */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'start',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Location
            </label>
            <div
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '20px',
                color: '#212126',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}
            >
              {data.location.split('\n').map((line, idx) => (
                <span key={idx}>{line}</span>
              ))}
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Label */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'start',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Label
            </label>
            <div className="flex flex-col gap-1 items-start">
              <button
                className="flex items-center px-3 py-1 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: '12px',
                  lineHeight: '20px',
                  fontWeight: 400,
                  color: '#212126',
                  border: '1px solid #E9E9EA',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  height: '32px'
                }}
              >
                Upload Label
              </button>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '14px',
                  color: '#69697A'
                }}
              >
                Upload for LOT: {data.lot || 'NA'}
              </span>
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* COA (Analysis) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'start',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Buy contract Number
            </label>
            <div
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '20px',
                color: '#212126',
                textAlign: 'left'
              }}
            >
              {data.buyContractNumber || 'NA'}
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Sale Contract Number */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'start',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Sale Contract Number
            </label>
            <div
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '20px',
                color: '#212126',
                textAlign: 'left'
              }}
            >
              {data.saleContractNumber || 'NA'}
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* COA (Analysis) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'start',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              COA (Analysis)
            </label>
            <div className="flex flex-col gap-1 items-start">
              <button
                className="flex items-center px-3 py-1 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: '12px',
                  lineHeight: '20px',
                  fontWeight: 400,
                  color: '#212126',
                  border: '1px solid #E9E9EA',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  height: '32px'
                }}
              >
                Upload Label
              </button>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '14px',
                  color: '#69697A'
                }}
              >
                Upload for LOT: {data.lot || 'NA'}
              </span>
            </div>
          </div>

          {/* Separator */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Last Updated */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'center',
              paddingBottom: '16px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Last Updated
            </label>
            <div
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '20px',
                color: '#212126',
                textAlign: 'left'
              }}
            >
              {formatDateToDDMMYYYY(data.lastUpdated)}
            </div>
          </div>

          {/* Separator for Status */}
          <div
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#E5E7EB',
              marginBottom: '16px'
            }}
          ></div>

          {/* Status */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '32px',
              alignItems: 'center',
              paddingBottom: '0px'
            }}
          >
            <label
              className="block text-sm font-medium"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '20px',
                color: '#69697A',
                textAlign: 'left'
              }}
            >
              Status
            </label>
            <div
              className="px-3 py-1 rounded-md"
              style={{
                borderRadius: '6px',
                backgroundColor: statusStyles.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'fit-content'
              }}
            >
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: statusStyles.text
                }}
              >
                {data.status}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div 
          className="flex items-center justify-end gap-3 p-6 border-t"
          style={{
            backgroundColor: '#F9FAFB',
            borderTop: '1px solid #E5E7EB'
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#333333',
              border: '1px solid #D1D5DB',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px'
            }}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              backgroundColor: '#212126',
              borderRadius: '8px'
            }}
          >
            Track Externally
          </button>
        </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default PeanutLotDetailsModal
